import { useMemo } from 'react';
import { AccountState, AccountType } from './api/account-types';
import { useAccounts } from './api/use-get-accounts';
/**
 * Gets the default group name for an account type when no offBudgetGroup is specified.
 *
 * @param accountType - The type of account
 * @returns The default group name for the account type
 */
function getDefaultGroupName(accountType) {
    switch (accountType) {
        case AccountType.CASH:
        case AccountType.CHECKING:
        case AccountType.SAVINGS:
            return 'Liquid Accounts';
        case AccountType.CREDIT_CARD:
            return 'Credit Cards';
        case AccountType.OFF_BUDGET_ASSET:
        case AccountType.OFF_BUDGET_LIABILITY:
            return 'Off Budget';
        default:
            return 'Other';
    }
}
/**
 * Processes a list of accounts and returns them grouped by their offBudgetGroup property.
 * Accounts can be mixed across any group name. If no offBudgetGroup is specified,
 * accounts are placed in default groups based on their type.
 *
 * Groups are automatically ordered by the position of their first (lowest-positioned) account.
 * Within each group, accounts are ordered by their position.
 *
 * @param accounts - Array of accounts to process
 * @returns Object containing all account groups in proper order
 */
export const getAccountGroups = (accounts) => {
    const activeAccounts = accounts
        .filter((account) => account.state === AccountState.ACTIVE)
        .sort((a, b) => a.position - b.position); // Sort by position first
    const groupMap = new Map();
    activeAccounts.forEach((account) => {
        // use the custom group name if provided, otherwise use the default for the account type
        const groupName = account.offBudgetGroup || getDefaultGroupName(account.type);
        if (!groupMap.has(groupName)) {
            groupMap.set(groupName, {
                name: groupName,
                accounts: [],
            });
        }
        const group = groupMap.get(groupName);
        group.accounts.push(account);
    });
    // Convert to array and sort groups by the position of their first account
    const groups = Array.from(groupMap.values()).sort((a, b) => {
        const aFirstPosition = a.accounts[0]?.position ?? Infinity;
        const bFirstPosition = b.accounts[0]?.position ?? Infinity;
        return aFirstPosition - bFirstPosition;
    });
    return {
        groups,
    };
};
/**
 * Legacy function for backward compatibility.
 * Processes a list of accounts and returns only off-budget groups.
 *
 * @param accounts - Array of accounts to process
 * @returns Object containing off-budget groups only
 */
export const getOffBudgetGroups = (accounts) => {
    const offBudgetAccounts = accounts.filter((account) => account.type === AccountType.OFF_BUDGET_ASSET || account.type === AccountType.OFF_BUDGET_LIABILITY);
    return getAccountGroups(offBudgetAccounts);
};
/**
 * Custom hook that returns a list of account select options grouped by their offBudgetGroup property.
 * Accounts can be mixed across any group. Only includes active accounts.
 *
 * @returns An array of SelectOptionGroup objects containing account options
 */
export function useAccountSelectOptions() {
    const accounts = useAccounts();
    return useMemo(() => {
        const { groups } = getAccountGroups(accounts);
        return groups.map((group) => ({
            label: group.name,
            options: group.accounts.map((account) => ({
                value: account.id,
                label: account.name,
            })),
        }));
    }, [accounts]);
}
/**
 * Custom hook that returns a list of off-budget account select options grouped by their offBudgetGroup.
 * Only includes active off-budget accounts (OFF_BUDGET_ASSET and OFF_BUDGET_LIABILITY).
 *
 * @returns An array of SelectOptionGroup objects containing off-budget account options
 */
export function useOffBudgetAccountSelectOptions() {
    const accounts = useAccounts();
    return useMemo(() => {
        const offBudgetAccounts = accounts.filter((account) => account.state === AccountState.ACTIVE &&
            (account.type === AccountType.OFF_BUDGET_ASSET || account.type === AccountType.OFF_BUDGET_LIABILITY));
        const { groups } = getAccountGroups(offBudgetAccounts);
        return groups.map((group) => ({
            label: group.name,
            options: group.accounts.map((account) => ({
                value: account.id,
                label: account.name,
            })),
        }));
    }, [accounts]);
}
/**
 * Predefined select options grouped into On Budget and Off Budget.
 */
export const accountTypeSelectOptions = [
    {
        label: 'On Budget',
        options: [
            {
                value: AccountType.CASH,
                label: 'Cash',
            },
            {
                value: AccountType.CHECKING,
                label: 'Checking',
            },
            {
                value: AccountType.SAVINGS,
                label: 'Savings',
            },
            {
                value: AccountType.CREDIT_CARD,
                label: 'Credit Card',
            },
        ],
    },
    {
        label: 'Off Budget',
        options: [
            {
                value: AccountType.OFF_BUDGET_ASSET,
                label: 'Asset',
            },
            {
                value: AccountType.OFF_BUDGET_LIABILITY,
                label: 'Liability',
            },
        ],
    },
];
